import axios from 'axios';
import { NO_COMPANY_SCOPE_REQUEST_CONFIG } from '../../hooks/dashboard';

/**
 * ROAR-4802 (AC9) — Profile write.
 *
 * Company-scoped SERVER-SIDE, exactly like the ROAR-4899 reads next door in
 * `api/queries/account.js`: the client sends no company identifier at all, and
 * `NO_COMPANY_SCOPE_REQUEST_CONFIG` (exported from hooks/dashboard.js by ROAR-4940) strips the
 * `company_id`/`selected_company_uuid` headers src/index.js sets at bootstrap. The P1.10
 * scope-resolver middleware resolves both the user and the company from the JWT.
 *
 * Importing that config pulls in hooks/dashboard.js's ESM-only `query-string` dependency, so any
 * test touching this module needs `jest.mock('query-string', () => ({ stringify: jest.fn() }))`.
 *
 * **RATIFIED 2026-09-28 (ROAR-4963)** against the endpoint ROAR-4964 shipped. The FE's earlier
 * proposal had four fields; the ratified contract has three, and only two of them are writable.
 *
 * The backend gate (`controllers/account/utils/profileUpdateGate.js`) accepts a STRICT ALLOW-LIST
 * of `{first_name, last_name, email}` and rejects any other key **on presence, whatever its
 * value**. Rejection is **all-or-nothing** — one bad key means no field is applied — so this
 * payload must never grow a field speculatively:
 *
 *  - `first_name` / `last_name` are the only writable columns (ROAR-4802 AC9: edit the full name).
 *    Blank, non-string, or over 255 characters is a 400.
 *  - `email` is allow-listed but IMMUTABLE, and **this client no longer sends it**. The gate
 *    guards its whole email branch with a `hasOwnProperty` check, so an absent `email` is skipped
 *    entirely and can never 400 — whereas a present one is compared against `users.email` and 400s
 *    ("email cannot be changed") the moment the two disagree. Email is the one key whose value the
 *    pane does not take from user input: it is seeded from `useValidateLogin`, so any drift
 *    between that cached session and the row would surface as a baffling failure on a save that
 *    never touched the address. Omitting it removes that failure mode outright. Sending it was
 *    only ever a compatibility choice — the backend allow-listed the key because this client used
 *    to send it, not because it needs it.
 *  - `company_name` is sent again, for a `customer_leader` who actually EDITED it (ROAR-4899,
 *    reversing the ROAR-4963 removal at the developer's direction). **This key requires a backend
 *    change to land first.** The ratified ROAR-4964 gate rejects it on presence, and rejection is
 *    all-or-nothing, so against the gate as shipped a leader's rename 400s and applies no field —
 *    the same failure ROAR-4963 removed, now re-entered deliberately and narrowed to the one save
 *    that means to rename. `company_name` also lives on `companies`, not `users`, so widening the
 *    allow-list is not enough on its own: the controller needs a write path to the company row.
 *    Until both exist, leave the field disabled rather than shipping a button that always fails.
 *    Callers that do not pass the key are unaffected — an absent one is skipped by the gate.
 *  - `role` is likewise rejected, and was never sent: AC3 ("Self-Role-Change Blocked") forbids a
 *    user changing their own role through any endpoint or form.
 *
 * The response is the backend's profile projection, read back through an attribute-scoped getter.
 * This client treats it as opaque and re-reads the user by invalidating `useValidateLogin`.
 *
 * @param {object} payload
 * @param {string} payload.first_name
 * @param {string} [payload.last_name] Omitted by the caller for a mononym; a blank one is a 400.
 * @param {string} [payload.company_name] Leader-only, and only when actually edited — see above.
 * @returns {Promise<object>}
 */
export const updateProfile = async ({ first_name, last_name, company_name }) => {
  const res = await axios.patch(
    '/account/profile',
    // The writable keys, nothing conditional beyond `last_name`'s and `company_name`'s absence.
    // Any OTHER key is a 400 that discards the whole save, so a field may only be added here
    // alongside a matching backend change. `email` is deliberately absent — see above.
    //
    // `company_name` is spread in only when the caller passes it, so the common saves — every
    // non-leader, and a leader who only renamed themselves — keep the exact two-key payload the
    // shipped gate already accepts, and cannot be broken by a gate that has not been widened yet.
    { first_name, last_name, ...(company_name !== undefined ? { company_name } : {}) },
    NO_COMPANY_SCOPE_REQUEST_CONFIG
  );
  return res.data;
};
